/* eslint-disable object-curly-newline */
import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import { getComparator, stableSort } from '../utils';
import EnhancedTableHead from '../EnhancedTableHead';
import { useLocalizedMessage } from 'util/useLocalizedMessage';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  table: {
    // minWidth: 750,
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
}));

export default function ProductsTable({
  headCells, rows, hidePagination, rowHandleClick, pagination,
}) {
  const classes = useStyles();
  const t = useLocalizedMessage();
  const [order, setOrder] = React.useState('desc');
  const [orderBy, setOrderBy] = React.useState('ctr');
  const [selected, setSelected] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(15);

  const controlled = Boolean(pagination);
  const effectivePage = controlled ? pagination.page : page;
  const effectiveRowsPerPage = controlled ? pagination.rowsPerPage : rowsPerPage;
  const rowsCount = controlled ? pagination.total : rows.length;
  const displayRows = controlled ? rows : stableSort(rows, getComparator(order, orderBy));

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = rows.map((n) => n.name);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (event, newPage) => {
    if (controlled) {
      pagination.onPageChange(newPage);
      return;
    }
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const value = parseInt(event.target.value, 10);
    if (controlled) {
      pagination.onRowsPerPageChange(value);
      return;
    }
    setRowsPerPage(value);
    setPage(0);
  };

  const isSelected = (name) => selected.indexOf(name) !== -1;

  return (
    <div className={classes.root}>

      <TableContainer>
        <Table
          className={classes.table}
          aria-labelledby="tableTitle"
          size="small"
          aria-label={t('table.accessible.products')}
        >
          <EnhancedTableHead
            headCells={headCells}
            classes={classes}
            numSelected={selected.length}
            order={order}
            orderBy={orderBy}
            onSelectAllClick={handleSelectAllClick}
            onRequestSort={handleRequestSort}
            rowCount={rowsCount}
          />
          <TableBody>
            {displayRows
              .slice(
                controlled ? 0 : effectivePage * effectiveRowsPerPage,
                controlled ? rows.length : effectivePage * effectiveRowsPerPage + effectiveRowsPerPage,
              )
              .map((row, index) => {
                const isItemSelected = isSelected(row.name);
                return (
                  <TableRow
                    onClick={(event) => rowHandleClick(event, row.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        rowHandleClick(event, row.id);
                      }
                    }}
                    hover
                    aria-checked={isItemSelected}
                    aria-label={row.accessibleName
                      ? t('table.products.open', { product: row.accessibleName })
                      : undefined}
                    tabIndex={0}
                    key={row.id || row.query}
                    selected={isItemSelected}
                    style={{ cursor: 'pointer' }}
                  >
                    {
                        headCells.map((e) => (
                          <TableCell
                            key={e.id}
                            align={e.numeric ? 'right' : 'left'}
                            component="th"
                          >
                            {row[e.id]}
                          </TableCell>
                        ))
                    }
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      {
          hidePagination
            ? null
            : (
              <TablePagination
                rowsPerPageOptions={[15, 25, 50, 100]}
                component="div"
                count={rowsCount}
                rowsPerPage={effectiveRowsPerPage}
                page={effectivePage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            )

      }
    </div>
  );
}

ProductsTable.defaultProps = {
  hidePagination: false,
  rowHandleClick: null,
  pagination: null,
};

ProductsTable.propTypes = {
  rowHandleClick: PropTypes.func,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  headCells: PropTypes.arrayOf(PropTypes.object).isRequired,
  hidePagination: PropTypes.bool,
  pagination: PropTypes.shape({
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
    onRowsPerPageChange: PropTypes.func.isRequired,
  }),
};
